import dayjs, { extend as dayJsExtend } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { ISbStoryData } from 'storyblok-js-client'

import { StoryblokImage } from 'components/blocks/StoryblokImage'
import { Button, Icon, Tag } from 'components/ui'
import { useTranslation } from 'lib/i18n'
import { Storyblok } from 'lib/storyblok'
import {
  OpenEveningStoryblok,
  RichTextClinicLocationStoryblok,
} from 'lib/storyblok/types'
import {
  getStoryblokCacheValue,
  setStoryblokCacheValue,
} from 'lib/utils/content'
import { GTMEvents, trackEvent } from 'lib/utils/gtm'
import { removeLeadingAndTrailingSlash, toCapitalize } from 'lib/utils/string'
import { cn } from 'lib/utils/tailwind'

import { Loader } from '../AllEntries/Loader'

dayJsExtend(customParseFormat)

type Props = {
  item: ISbStoryData<OpenEveningStoryblok>
  variant?: 'cover' | 'big' | 'small'
  className?: string
}

export const OpenEveningThumb = ({
  item,
  variant = 'small',
  className,
  ...props
}: Props): JSX.Element => {
  const [isClinicLoading, setIsClinicLoading] = useState<boolean>(false)
  const [clinic, setClinic] = useState<RichTextClinicLocationStoryblok | null>(
    null
  )

  const { i18n, locale } = useTranslation()
  const { isPreview, query } = useRouter()
  const router = useRouter()

  const handleTrackEvent = () => {
    trackEvent(GTMEvents.OpenEvening, {
      'tfp-title': item.content.title || 'n/a',
      'tfp-location': item.content.location || 'n/a',
      'tfp-date-time': item.content.date_time || 'n/a',
      'tfp-type': item.content.type,
    })
  }

  const isExternal = item?.content?.link?.linktype === 'url'

  const dayJsDate = dayjs(
    `${item.content.date_time}+00:00`,
    'YYYY-MM-DD HH:mmZ'
  )
  const day = dayJsDate.locale(locale).format('dddd')
  const date = dayJsDate.locale(locale).format('ll')
  const time = dayJsDate.locale(locale).format('HH:mm')
  // const isInLessThan7Days = dayJsDate.subtract(7, 'days').isBefore(new Date())

  const types = {
    clinic: i18n('eveningType.clinic'),
    online: i18n('eveningType.online'),
    other: i18n('eveningType.other'),
  }

  const fetch = useCallback(async () => {
    setIsClinicLoading(true)

    try {
      const {
        data: { stories, cv },
      } = await Storyblok.get('cdn/stories', {
        version: isPreview ? 'draft' : 'published',
        sort_by: 'updated_at:desc',
        content_type: 'clinic-location',
        excluding_fields: 'body,seo',
        cv: getStoryblokCacheValue(),
        filter_query: {
          name: { in: item.name },
        },
      })

      if (stories.length === 1) {
        setClinic(stories[0])
      }
      setStoryblokCacheValue(cv)
    } catch (error) {
      console.error('Error fetching clinic:', error)
    }

    setIsClinicLoading(false)
  }, [isPreview, query])

  useEffect(() => {
    if (router.isReady && variant === 'cover') {
      fetch()
    }
  }, [fetch, router.isReady])

  return (
    <li
      className={cn(
        'flex flex-col relative bg-background-dark rounded-5 overflow-hidden min-h-40 p-4 md:flex-row',
        variant === 'cover' && 'md:items-center md:justify-between gap-5',
        className
      )}
      {...props}
    >
      <div
        className={cn(
          'flex flex-col flex-1 gap-6',
          variant === 'big' && 'md:max-w-[304px]'
        )}
      >
        <div className="flex flex-col gap-4">
          <Tag icon={item.content.type === 'online' ? 'world' : 'map-pointer'}>
            {toCapitalize(item.content.type)}
          </Tag>

          <h2 className="font-regular text-twenty text-foreground">
            {item.content?.title || item?.name}
          </h2>

          <div className="w-full">
            {item.content.date_time && (
              <div className="flex items-center mb-1.5">
                <Icon icon="calendar" className="mr-2" />
                <span className="font-light text-eighteen text-foreground/80">
                  {day.slice(0, 3)}, {date}
                </span>
                <div className="flex size-0.5 bg-foreground/80 my-0 mx-1" />
                <span className="font-light text-eighteen text-foreground/80">
                  {time}
                </span>
              </div>
            )}

            {item.content.location ? (
              <div className="flex items-center">
                <Icon
                  icon={
                    item.content.type === 'online' ? 'world' : 'map-pointer'
                  }
                  className="mr-2"
                />
                <span className="font-light text-eighteen text-foreground/80">
                  {item.content.location}
                  {` (${types[item?.content?.type]})`}
                </span>
              </div>
            ) : (
              <div className="flex items-center">
                <Icon icon="map-pointer" className="mr-2" />
                <span className="font-light text-eighteen text-foreground/80">
                  {types[item.content.type]}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-auto w-fit flex-grow-0">
          <Button
            as="a"
            variant="tertiary"
            onClick={handleTrackEvent}
            {...(isExternal
              ? {
                  icon: 'external',
                  iconPosition: 'right',
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  href: item?.content?.link?.cached_url,
                }
              : {
                  iconPosition: 'right',
                  icon: 'arrow-right',
                  href: `/${removeLeadingAndTrailingSlash(
                    item?.content?.link?.cached_url || item.full_slug
                  )}`,
                })}
          >
            {i18n('eventSignUp')}
          </Button>
        </div>
      </div>

      {variant === 'cover' && (
        <>
          {clinic ? (
            <div className="flex overflow-hidden w-full md:max-w-[284px] h-[200px] md:h-full md:min-h-[235px] rounded-3">
              <StoryblokImage
                asset={clinic.content.image}
                className="object-cover"
              />
            </div>
          ) : isClinicLoading ? (
            <Loader />
          ) : null}
        </>
      )}
    </li>
  )
}
